import { getLocalTimeZone, ZonedDateTime } from "@internationalized/date";
import cx from "classnames";
import { endOfDay, startOfDay } from "date-fns/esm";
import { useCallback, useMemo, useState } from "react";
import {
  DateInput,
  DateRangePicker,
  DateRangePickerProps,
  Dialog,
  Group,
  Label,
  Popover,
  RangeCalendar,
  TimeField,
  TimeValue,
} from "react-aria-components";

import { Calendar, ChevronDown } from "components/icons/generated";
import { formatDate } from "utils/date";

import BaseActionButton from "../BaseAction/Button";
import Box from "../Box";
import ButtonIcon from "../ButtonIcon";
import Icon from "../Icon";
import Tab from "../Tab";
import DatepickerCalendarContent from "./CalendarContent";
import CustomDateSegment from "./CustomDateSegment";
import { formatAbsoluteToZonedDateTime, getDateParts } from "./helpers";
import styles from "./styles.module.css";
import { DatepickerRangeProps } from "./types";

const adEraStart = new Date(Date.parse("0001-01-01"));

const DatepickerRange = <T extends string>({
  startDate = new Date(),
  endDate = new Date(),
  onChange,
  minDate = adEraStart,
  maxDate,
  periods,
  initialPeriod,
  withTime,
  disabled,
  hasError,
  isButton,
  buttonSize = "small",
  placement = "bottom end",
  renderTimezone,
  granularity = "minute",
  fullWidth = false,
}: DatepickerRangeProps<T>) => {
  const [periodId, setPeriodId] = useState<T | undefined>(initialPeriod);

  const minZonedDate = formatAbsoluteToZonedDateTime(minDate, renderTimezone);
  const maxZonedDate = maxDate && formatAbsoluteToZonedDateTime(maxDate, renderTimezone);

  const { zonedDateTime: startValueDate, time: startTime } = getDateParts(
    startDate,
    renderTimezone
  );

  const { zonedDateTime: endValueDate, time: endTime } = getDateParts(endDate, renderTimezone);

  const onStartTimeChange = useCallback(
    (value: TimeValue | null) => {
      const start = startValueDate.set({ hour: value?.hour, minute: value?.minute }).toDate();

      onChange?.({ start, end: endValueDate.toDate() }, periodId);
    },
    [endValueDate, onChange, periodId, startValueDate]
  );

  const onEndTimeChange = useCallback(
    (value: TimeValue | null) => {
      const end = endValueDate.set({ hour: value?.hour, minute: value?.minute }).toDate();

      onChange?.({ start: startValueDate.toDate(), end }, periodId);
    },
    [endValueDate, onChange, periodId, startValueDate]
  );

  const onRangePickerDateChange = useCallback<
    NonNullable<DateRangePickerProps<ZonedDateTime>["onChange"]>
  >(
    (value) => {
      if (value?.start.era !== "AD" || value?.end.era !== "AD") {
        return;
      }

      if (periodId !== undefined) {
        setPeriodId(undefined);
      }

      const range = withTime
        ? value
        : {
            start: value.start.set({ hour: 0, minute: 0, second: 0, millisecond: 0 }),
            end: value.end.set({ hour: 23, minute: 59, second: 59, millisecond: 999 }),
          };

      onChange?.({
        start: range.start.toDate(),
        end: range.end.toDate(),
      });
    },
    [onChange, periodId, withTime]
  );

  const onTabRangeChange = useCallback(
    (periodId: T) => {
      setPeriodId(periodId);
      const period = periods?.[periodId];

      if (period) {
        const { start, end } = period.range();
        onChange?.({ start, end }, periodId);
      }
    },
    [onChange, periods]
  );

  const setCustomRange = useCallback(() => {
    setPeriodId(undefined);
    onChange?.({ start: startOfDay(new Date()), end: endOfDay(new Date()) });
  }, [onChange]);

  const buttonTitle = useMemo(() => {
    if (periodId && periods?.[periodId]) {
      return periods[periodId].label;
    }

    const displayTimezone = renderTimezone ?? getLocalTimeZone();
    const format = withTime ? "default" : "dateOnly";

    return `${formatDate({ date: startDate, timezone: displayTimezone, format })} - ${formatDate({ date: endDate, timezone: displayTimezone, format })}`;
  }, [endDate, periodId, periods, renderTimezone, startDate, withTime]);

  return (
    <DateRangePicker
      maxValue={maxZonedDate}
      minValue={minZonedDate}
      onChange={onRangePickerDateChange}
      value={{ start: startValueDate, end: endValueDate }}
      granularity={granularity}
      aria-label="Choose date range"
    >
      <Box direction="column" gap="small" align="start">
        {!isButton && (
          <Group
            isDisabled={disabled}
            isInvalid={hasError}
            className={cx(styles.input, styles.withOpenButton, {
              [styles.fullWidth]: fullWidth,
            })}
          >
            <Box direction="row" align="center" gap="medium" fullWidth>
              <Box direction="row" align="center" gap="small">
                <DateInput className={styles.inputInner} slot="start">
                  {(segment) => <CustomDateSegment segment={segment} value={startValueDate} />}
                </DateInput>
                -
                <DateInput className={styles.inputInner} slot="end">
                  {(segment) => <CustomDateSegment segment={segment} value={endValueDate} />}
                </DateInput>
              </Box>

              <ButtonIcon icon={ChevronDown} variant="ghost" className={styles.openButton}>
                Open datepicker
              </ButtonIcon>
            </Box>
          </Group>
        )}
        {isButton && (
          <Group aria-label="Open datepicker" isDisabled={disabled}>
            <BaseActionButton
              aria-label="Open datepicker"
              disabled={disabled}
              className={cx(styles.button, styles[buttonSize])}
            >
              <Icon src={Calendar} />
              {buttonTitle}
            </BaseActionButton>
          </Group>
        )}
      </Box>
      <Popover placement={placement}>
        <Dialog className={styles.dialog}>
          {periods && (
            <Box className={styles.periods} padding="large" gap="medium" direction="column">
              {Object.keys(periods).map((id) => (
                <Tab
                  isActive={periodId === id}
                  onClick={onTabRangeChange}
                  id={id as T}
                  key={id}
                  label={periods[id as T].label}
                  justify="start"
                />
              ))}

              <Tab
                isActive={periodId === undefined}
                onClick={setCustomRange}
                id="custom"
                label="Custom"
                justify="start"
              />
            </Box>
          )}
          <Box direction="column" align="center" justify="center">
            {/* FYI: key allows to rerender calendar and move month to proper one when selecting period */}
            <RangeCalendar key={periodId} className={styles.calendar}>
              <DatepickerCalendarContent />
            </RangeCalendar>
            {withTime && (
              <Box gap="medium" className={styles.timeWrapper}>
                <TimeField
                  className={styles.timeField}
                  value={startTime}
                  onChange={onStartTimeChange}
                >
                  <Label className={styles.timeLabel}>From time</Label>
                  <DateInput className={cx(styles.input, styles.timeInput)}>
                    {(segment) => <CustomDateSegment segment={segment} value={startValueDate} />}
                  </DateInput>
                </TimeField>

                <div className={styles.timeGap}>-</div>
                <TimeField className={styles.timeField} value={endTime} onChange={onEndTimeChange}>
                  <Label className={styles.timeLabel}>To time</Label>
                  <DateInput className={cx(styles.input, styles.timeInput)}>
                    {(segment) => <CustomDateSegment segment={segment} value={endValueDate} />}
                  </DateInput>
                </TimeField>
              </Box>
            )}
          </Box>
        </Dialog>
      </Popover>
    </DateRangePicker>
  );
};

DatepickerRange.displayName = "DS.Datepicker.Range";

export default DatepickerRange;
